/**
 * BatteryComparisonChart Component
 *
 * Three-series line chart: Fixed tariff, Agile (passive switch), and
 * Agile + Battery (perfect-foresight DP optimum). Green range-area fills
 * the band between Agile and Agile+Battery to visualise the battery-
 * specific saving on top of the tariff switch.
 *
 * Data source: useBatterySimulation hook → battery-simulate edge function.
 * Excludes VAT (same convention as the Agile comparison chart).
 */

import { useMemo, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import TariffSelector from '@/components/energy/TariffSelector'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

const FIXED_COLOR = '#f59e0b'    // amber
const AGILE_COLOR = '#0ea5e9'    // sky blue
const BATTERY_COLOR = '#16a34a'  // green — DP-optimal Agile+battery
const SAVING_COLOR = '#22c55e'   // green — battery-specific saving band

function formatMonth(month) {
  return new Date(month).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

function formatCurrency(value) {
  if (value == null) return '—'
  if (Math.abs(value) >= 1000) return `£${(value / 1000).toFixed(1)}k`
  return `£${value.toFixed(0)}`
}

function formatPencePerKwh(value) {
  if (value == null) return '—'
  return `${value.toFixed(1)}p`
}

function CostTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })

  const saving = row.agile - row.battery
  const savingLabel = saving >= 0
    ? `£${saving.toFixed(0)} extra saved by battery`
    : `£${Math.abs(saving).toFixed(0)} worse than Agile passive` // should not happen

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-52">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <Row color={FIXED_COLOR} label="Fixed" value={`£${row.fixed.toFixed(0)}`} />
      <Row color={AGILE_COLOR} label="Agile" value={`£${row.agile.toFixed(0)}`} />
      <Row color={BATTERY_COLOR} label="Agile + Battery" value={`£${row.battery.toFixed(0)}`} />
      <div className="border-t border-neutral-200 mt-2 pt-2 text-sm">
        <span className="text-green-600">{savingLabel}</span>
      </div>
      {row.coverage_pct < 100 && (
        <p className="text-xs text-neutral-500 mt-2">
          Coverage: {row.coverage_pct.toFixed(1)}%
        </p>
      )}
    </div>
  )
}

function UnitRateTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-52">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <Row color={FIXED_COLOR} label="Fixed" value={`${row.fixed.toFixed(1)}p/kWh`} />
      <Row color={AGILE_COLOR} label="Agile" value={`${row.agile.toFixed(1)}p/kWh`} />
      <Row color={BATTERY_COLOR} label="Agile + Battery" value={`${row.battery.toFixed(1)}p/kWh`} />
      <p className="text-[10px] text-neutral-500 mt-2">
        Effective unit rate = total cost ÷ kWh consumed (standing charges included)
      </p>
    </div>
  )
}

function Row({ color, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm mt-1 first:mt-0">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-neutral-600">{label}</span>
      </div>
      <span className="font-medium text-neutral-900">{value}</span>
    </div>
  )
}

export default function BatteryComparisonChart({ data, isLoading, vatRate = 0, tariff, tariffLabel, onTariffChange }) {
  const [mode, setMode] = useState('cost') // 'cost' | 'unit'

  const chartData = useMemo(() => {
    if (!data?.monthly?.length) return []
    // Cost mode: show inc-VAT to match the Overview tab and actual bills.
    // p/kWh mode: leave excl-VAT — tariffs are quoted excl-VAT so the 22.8p
    // Fixed reference reads correctly.
    const costMult = 1 + (vatRate ?? 0)
    return data.monthly.map(row => {
      const kwh = parseFloat(row.kwh_consumed ?? 0) || 0
      const fixedCost = ((row.fixed_total_pence ?? 0) / 100) * costMult
      const agileCost = ((row.agile_total_pence ?? 0) / 100) * costMult
      const batteryCost = ((row.agile_battery_total_pence ?? 0) / 100) * costMult

      // Unit-rate denominators must use excl-VAT costs since p/kWh is
      // advertised excl-VAT.
      const fixedUnit = (row.fixed_total_pence ?? 0) / 100
      const agileUnit = (row.agile_total_pence ?? 0) / 100
      const batteryUnit = (row.agile_battery_total_pence ?? 0) / 100

      const fixed = mode === 'cost' ? fixedCost : (kwh > 0 ? (fixedUnit * 100) / kwh : 0)
      const agile = mode === 'cost' ? agileCost : (kwh > 0 ? (agileUnit * 100) / kwh : 0)
      const battery = mode === 'cost' ? batteryCost : (kwh > 0 ? (batteryUnit * 100) / kwh : 0)

      return {
        month: row.month,
        fixed,
        agile,
        battery,
        coverage_pct: parseFloat(row.coverage_pct ?? 0),
        // Green band between Agile and Agile+Battery (battery always
        // saves vs Agile-passive — the DP cannot choose to use more grid).
        savingBand: battery < agile ? [battery, agile] : null,
      }
    })
  }, [data, mode, vatRate])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fixed vs Agile vs Agile + Battery</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[360px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fixed vs Agile vs Agile + Battery</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[360px] text-neutral-500">
            No simulation data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const isCost = mode === 'cost'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>Fixed vs {tariffLabel ?? 'Agile'} vs {tariffLabel ?? 'Agile'} + Battery</CardTitle>
          <div className="flex items-center gap-3">
            {onTariffChange && (
              <TariffSelector value={tariff} onChange={onTariffChange} />
            )}
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(value) => value && setMode(value)}
            size="sm"
          >
            <ToggleGroupItem value="cost" className="text-xs px-3">
              Cost
            </ToggleGroupItem>
            <ToggleGroupItem value="unit" className="text-xs px-3">
              p/kWh
            </ToggleGroupItem>
          </ToggleGroup>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Battery dispatch is perfect-foresight DP — the ceiling of achievable
          Agile+battery savings. A realisable day-ahead dispatcher would be lower.
          {' '}
          {mode === 'cost'
            ? 'Costs shown inc VAT.'
            : 'Unit rates shown excl VAT (standing charge amortised over kWh consumed).'}
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={isCost ? formatCurrency : formatPencePerKwh}
            />
            <Tooltip content={isCost ? <CostTooltip /> : <UnitRateTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="savingBand"
              stroke="none"
              fill={SAVING_COLOR}
              fillOpacity={0.18}
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="fixed"
              name="Fixed"
              stroke={FIXED_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="agile"
              name="Agile"
              stroke={AGILE_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="battery"
              name="Agile + Battery"
              stroke={BATTERY_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
